<div class="flex min-h-screen items-center justify-center px-4 py-8">
  <div class="flex w-full max-w-md flex-col gap-6">
    <app-logo [widthInPx]="120" />

    <div hlmCard>
      <div hlmCardHeader>
        <h1 hlmCardTitle>Bei deinem Konto anmelden</h1>
        <p hlmCardDescription>Gib deine E-Mail-Adresse ein, um dich anzumelden</p>
      </div>

      <div hlmCardContent class="space-y-6">
        @if (pendingGroupName) {
          <div hlmAlert>
            <ng-icon name="lucideUsers" />
            <p hlmAlertTitle>Einladung zu {{ pendingGroupName }}</p>
            <p hlmAlertDescription>Melde dich an, um dem Netzwerk beizutreten.</p>
          </div>
        }

        <!-- Redirect: the account is on its way somewhere else -->
        @if (redirectMessage) {
          <div class="flex flex-col items-center gap-3 py-8 text-center">
            <hlm-spinner class="text-[length:--spacing(8)]" />
            <div>
              <p class="text-sm font-medium">{{ redirectMessage }}</p>
              <p hlmMuted class="text-sm">Einen Moment bitte…</p>
            </div>
          </div>
        }

        <!-- Google credential without a matching account -->
        @if (showGoogleChoice && !redirectMessage) {
          <div hlmAlert>
            <ng-icon name="lucideInfo" />
            <p hlmAlertTitle>Noch kein Konto gefunden</p>
            <p hlmAlertDescription>
              Für dieses Google-Konto existiert noch kein Schenkwerk-Konto. Was möchtest du tun?
            </p>
          </div>

          <div class="flex flex-col gap-2">
            <button hlmBtn (click)="startGoogleLinkLogin()">
              Mit bestehendem Konto verknüpfen
            </button>
            <button hlmBtn variant="outline" (click)="createAccountWithGoogle()">
              Neues Konto erstellen
            </button>
            <button hlmBtn variant="ghost" size="sm" (click)="cancelGoogleFlow()">Abbrechen</button>
          </div>
        }

        @if (!redirectMessage && !showGoogleChoice) {
          <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-6">
            <div hlmField>
              <label hlmFieldLabel for="email">E-Mail</label>
              <input
                hlmInput
                id="email"
                type="email"
                inputmode="email"
                formControlName="email"
                autocomplete="email"
                placeholder="name@beispiel.de"
                class="w-full"
                [attr.aria-invalid]="submitted && f['email'].errors ? 'true' : null" />
              @if (submitted && f['email'].errors) {
                <p hlmFieldDescription class="text-destructive">
                  @if (f['email'].errors['required']) {
                    E-Mail ist erforderlich
                  } @else {
                    Bitte gib eine gültige E-Mail-Adresse ein
                  }
                </p>
              }
            </div>

            <div hlmField>
              <div class="flex items-center">
                <label hlmFieldLabel for="password">Passwort</label>
                <a
                  routerLink="/forgot-password"
                  class="ml-auto text-sm underline-offset-4 hover:underline">
                  Passwort vergessen?
                </a>
              </div>

              <hlm-input-group class="w-full">
                <input
                  hlmInputGroupInput
                  id="password"
                  [type]="showPassword ? 'text' : 'password'"
                  formControlName="password"
                  autocomplete="current-password"
                  [attr.aria-invalid]="submitted && f['password'].errors ? 'true' : null" />
                <hlm-input-group-addon align="inline-end">
                  <button
                    hlmInputGroupButton
                    type="button"
                    [attr.aria-label]="showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'"
                    (click)="togglePasswordVisibility()">
                    <ng-icon [name]="showPassword ? 'lucideEyeOff' : 'lucideEye'" />
                  </button>
                </hlm-input-group-addon>
              </hlm-input-group>

              @if (submitted && f['password'].errors) {
                <p hlmFieldDescription class="text-destructive">Passwort ist erforderlich</p>
              }
            </div>

            @if (error) {
              <div hlmAlert variant="destructive">
                <ng-icon name="lucideTriangleAlert" />
                <p hlmAlertTitle>{{ error }}</p>
              </div>
            }

            <!-- Link mode: password login attaches the Google account -->
            @if (googleLinkMode) {
              <div hlmAlert>
                <ng-icon name="lucideInfo" />
                <p hlmAlertDescription>
                  Melde dich mit E-Mail und Passwort an, um dein Google-Konto damit zu verknüpfen.
                  Danach kannst du dich direkt über Google anmelden.
                </p>
                <button
                  hlmBtn
                  variant="link"
                  size="xs"
                  class="mt-1 px-0"
                  (click)="cancelGoogleFlow()">
                  Abbrechen
                </button>
              </div>
            }

            @if (showResendVerification) {
              <div hlmAlert>
                <ng-icon name="lucideMail" />
                <p hlmAlertTitle>Account nicht aktiviert</p>
                <p hlmAlertDescription>
                  Überprüfe dein E-Mail-Postfach oder fordere eine neue Bestätigungs-E-Mail an.
                </p>
                <button
                  hlmBtn
                  variant="outline"
                  size="sm"
                  type="button"
                  class="mt-2"
                  [disabled]="resendLoading"
                  (click)="resendVerificationEmail()">
                  @if (resendLoading) {
                    <hlm-spinner />
                    Wird gesendet…
                  } @else {
                    Bestätigungs-E-Mail erneut senden
                  }
                </button>
                @if (resendSuccess) {
                  <p hlmAlertDescription class="mt-2">Bestätigungs-E-Mail wurde gesendet.</p>
                }
              </div>
            }

            <div class="flex flex-col gap-4">
              <button hlmBtn type="submit" class="w-full" [disabled]="loading">
                @if (loading) {
                  <hlm-spinner />
                  Anmelden…
                } @else {
                  Login
                }
              </button>

              <div [hidden]="!googleAvailable || googleLinkMode" class="flex flex-col gap-4">
                <div
                  class="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                  <span hlmMuted class="bg-card relative z-10 px-2">oder</span>
                </div>

                <!-- Google's own button, rendered by GIS. It has to stay visible and
                     unobscured: behind an overlay or opacity trick Chrome rejects the
                     FedCM credential request, so the click does nothing. -->
                <div #googleBtn class="flex justify-center [&>*]:max-w-full"></div>
              </div>
            </div>
          </form>

          <p class="text-center text-sm">
            Du hast noch keinen Account?
            <a
              routerLink="/register"
              [queryParams]="{ redirect: redirectParam, groupName: pendingGroupName }"
              class="underline underline-offset-4">
              Jetzt registrieren
            </a>
          </p>
        }
      </div>
    </div>
  </div>
</div>
